import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";

import { coalesce, doNothing } from "@kazoohr/helpers";

import { useApplicationStateQuery } from "../../../generated/apiHooks";

export const MAINTENANCE_MODE_POLL_INTERVAL_MS = 30_000;

const MaintenanceModeContext = createContext<{
  maintenanceModeEnabled: boolean;
  checkMaintenanceMode: () => void;
}>({ maintenanceModeEnabled: false, checkMaintenanceMode: doNothing });

export function MaintenanceModeProvider({ children }: PropsWithChildren) {
  const { data, startPolling, stopPolling, refetch } = useApplicationStateQuery(
    {
      fetchPolicy: "network-only",
      onError: doNothing,
    }
  );
  const maintenanceModeEnabled = coalesce(
    data?.applicationState.maintenanceModeEnabled,
    false
  );

  useEffect(() => {
    if (!maintenanceModeEnabled) {
      return undefined;
    }
    startPolling(MAINTENANCE_MODE_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [maintenanceModeEnabled, startPolling, stopPolling]);

  const value = useMemo(
    () => ({
      maintenanceModeEnabled,
      checkMaintenanceMode: () => {
        refetch().catch(doNothing);
      },
    }),
    [maintenanceModeEnabled, refetch]
  );

  return (
    <MaintenanceModeContext.Provider value={value}>
      {children}
    </MaintenanceModeContext.Provider>
  );
}

export function useMaintenanceMode(): boolean {
  return useContext(MaintenanceModeContext).maintenanceModeEnabled;
}

export function useCheckMaintenanceMode(): () => void {
  return useContext(MaintenanceModeContext).checkMaintenanceMode;
}
